import { useState } from 'react';

import { authOptions } from 'api/auth';
import { User } from 'api/auth/api.pb';
// Consume the client generated by the shared APIs library.
import { MFAService } from 'api/shared/mfa/v1/api.pb';
import { successToast } from 'app/components/Toast';
import { useKeycloakContext } from 'containers/App/keycloak/context';
import {
  getMFAError,
  needsMFAReauthentication,
} from 'containers/UserProfile/mfaUtils';
import { useMFAStatus } from 'containers/UserProfile/useMFAStatus';

// The server re-checks organization-admin membership and a recent sign-in for every reset.
export function useResetUserMFA(onReset: () => void, enabled: boolean) {
  const { data } = useMFAStatus(enabled);
  const { reauthenticateForMFA } = useKeycloakContext();
  const [resettingId, setResettingId] = useState<string>();
  const [failure, setFailure] = useState<{ id?: string; message: string }>();

  // Resolves true once the credential is removed so the confirmation can close.
  const reset = async (user: User) => {
    setResettingId(user.id);
    setFailure(undefined);
    try {
      await MFAService.ResetUser({ user_id: user.id }, authOptions());
      successToast({
        title: `Two-factor authentication reset for ${user.username}`,
        description: 'They will set up a new authenticator at next sign-in.',
      });
      onReset();
      return true;
    } catch (err) {
      if (needsMFAReauthentication(err)) {
        await reauthenticateForMFA('users');
        return false;
      }
      // The app-wide error toast is disabled, so the confirmation shows the failure.
      setFailure({ id: user.id, message: getMFAError(err) });
      return false;
    } finally {
      setResettingId(undefined);
    }
  };

  return {
    canReset: !!data?.can_manage_policy,
    resettingId,
    errorFor: (id?: string) =>
      failure && failure.id === id ? failure.message : undefined,
    reset,
  };
}
